import { AlertTriangle, Minus, Plus } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { BatchOptionSelect } from '@/app/components/inventory/BatchOptionSelect'
import { InventorySourceModal } from '@/app/components/pos/modals/InventorySourceModal'
import { Button } from '@/app/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select'
import { InventoryItem } from '@/app/services/inventory.service'
import type { InventoryAllocation, InventorySource } from '@/app/services/pos.service'
import { productService } from '@/app/services/product.service'

interface AdjustModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedItem: InventoryItem | null
  productId: number | null
  sucursalId: number | null
  requiresLot: boolean
  onApplyAdjustment: (data: {
    item: string
    type: 'add' | 'subtract'
    quantity: string
    reason: string
    batch_mode?: 'new' | 'existing'
    product_batch_id?: number
    batch_number?: string
    expiration_date?: string
    allocations?: InventoryAllocation[]
  }) => void
}

const COMMON_REASONS = [
  'Merma',
  'Devolución de cliente',
  'Corrección de inventario',
  'Caducidad',
  'Robo / Pérdida',
  'Compra / Recepción',
]

export function AdjustModal({
  open,
  onOpenChange,
  selectedItem,
  productId,
  sucursalId,
  requiresLot,
  onApplyAdjustment,
}: AdjustModalProps) {
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'subtract'>('add')
  const [adjustmentQuantity, setAdjustmentQuantity] = useState('')
  const [adjustmentReason, setAdjustmentReason] = useState('')
  const [batchMode, setBatchMode] = useState<'new' | 'existing'>('new')
  const [batchNumber, setBatchNumber] = useState('')
  const [expirationDate, setExpirationDate] = useState('')
  const [existingBatchId, setExistingBatchId] = useState('')
  const [batchSources, setBatchSources] = useState<InventorySource[]>([])
  const [batchSourcesLoading, setBatchSourcesLoading] = useState(false)
  const [batchSourcesError, setBatchSourcesError] = useState(false)
  const [allocations, setAllocations] = useState<InventoryAllocation[]>([])
  const [showSources, setShowSources] = useState(false)
  const batchRequestRef = useRef(0)

  const currentStock = selectedItem?.stock ?? 0
  const parsedQty = parseInt(adjustmentQuantity) || 0
  const newStock =
    adjustmentType === 'add' ? currentStock + parsedQty : currentStock - parsedQty
  const willGoNegative = adjustmentType === 'subtract' && newStock < 0
  const loadSources = useCallback((id: number, quantity: number) => {
    if (!sucursalId) return Promise.reject(new Error('Selecciona una sucursal.'))
    return productService.getInventorySources(id, sucursalId, quantity)
  }, [sucursalId])

  const loadBatchOptions = useCallback(async () => {
    if (!requiresLot || !productId || !sucursalId) {
      batchRequestRef.current += 1
      setBatchSourcesLoading(false)
      return
    }

    const requestId = ++batchRequestRef.current
    setBatchSourcesLoading(true)
    setBatchSourcesError(false)
    try {
      const snapshot = await productService.getInventorySources(productId, sucursalId, 1)
      if (requestId !== batchRequestRef.current) return
      setBatchSources(snapshot.sources.filter((source) => source.source_type === 'batch' && !source.is_expired))
    } catch {
      if (requestId !== batchRequestRef.current) return
      setBatchSources([])
      setBatchSourcesError(true)
    } finally {
      if (requestId === batchRequestRef.current) setBatchSourcesLoading(false)
    }
  }, [productId, requiresLot, sucursalId])

  useEffect(() => {
    setExistingBatchId('')
    setBatchSources([])
    setBatchSourcesError(false)
    if (open) void loadBatchOptions()
  }, [loadBatchOptions, open])

  useEffect(() => {
    setAllocations([])
  }, [adjustmentQuantity, adjustmentType])

  const handleAdjustmentTypeChange = (nextType: 'add' | 'subtract') => {
    if (nextType === adjustmentType) return

    setAdjustmentType(nextType)
    setExistingBatchId('')
  }

  const handleApply = () => {
    if (selectedItem) {
      onApplyAdjustment({
        item: selectedItem.name,
        type: adjustmentType,
        quantity: adjustmentQuantity,
        reason: adjustmentReason,
        ...(requiresLot && adjustmentType === 'add' ? {
          batch_mode: batchMode,
          ...(batchMode === 'existing'
            ? { product_batch_id: Number(existingBatchId) }
            : { batch_number: batchNumber.trim(), expiration_date: expirationDate }),
        } : {}),
        ...(requiresLot && adjustmentType === 'subtract' ? { allocations } : {}),
      })
    }
    setAdjustmentQuantity('')
    setAdjustmentReason('')
  }

  const handleClose = (val: boolean) => {
    if (!val) {
      batchRequestRef.current += 1
      setAdjustmentQuantity('')
      setAdjustmentReason('')
      setAdjustmentType('add')
      setBatchMode('new')
      setBatchNumber('')
      setExpirationDate('')
      setExistingBatchId('')
      setBatchSources([])
      setBatchSourcesError(false)
      setAllocations([])
    }
    onOpenChange(val)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ajuste de Stock</DialogTitle>
          <DialogDescription className="sr-only">
            Agregar o restar unidades del inventario con motivo
          </DialogDescription>
        </DialogHeader>

        {selectedItem && (
          <div className="space-y-4">
            {/* Tarjeta del producto */}
            <div className="rounded-lg border border-border bg-primary/5 px-4 py-3 overflow-hidden">
              <p className="text-sm font-semibold text-inherit break-words line-clamp-2" title={selectedItem.name}>{selectedItem.name}</p>
              <p className="mt-1 text-xs text-inherit opacity-70">
                Stock actual:{' '}
                <span className="font-medium text-inherit">{currentStock} unidades</span>
              </p>
            </div>

            {requiresLot && adjustmentType === 'add' && (
              <div className="space-y-3 rounded-lg border border-slate-200 p-3">
                <label className="text-sm font-medium text-slate-700">Lote de entrada</label>
                <Select
                  value={batchMode}
                  onValueChange={(value) => {
                    setBatchMode(value as 'new' | 'existing')
                    setExistingBatchId('')
                  }}
                >
                  <SelectTrigger aria-label="Modo de lote" className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Crear lote nuevo</SelectItem>
                    <SelectItem value="existing">Agregar a lote existente</SelectItem>
                  </SelectContent>
                </Select>
                {batchMode === 'new' ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input value={batchNumber} onChange={(event) => setBatchNumber(event.target.value)} placeholder="Número de lote" />
                    <Input type="date" min={new Date().toISOString().slice(0, 10)} value={expirationDate} onChange={(event) => setExpirationDate(event.target.value)} />
                  </div>
                ) : (
                  <BatchOptionSelect
                    options={batchSources.flatMap((source) => source.batch_id ? [{
                      id: source.batch_id,
                      batchNumber: source.batch_number ?? `Lote #${source.batch_id}`,
                      expirationDate: source.expiration_date ?? 'Sin caducidad',
                      physical: source.physical,
                    }] : [])}
                    value={existingBatchId}
                    onChange={(batchId) => setExistingBatchId(batchId ? String(batchId) : '')}
                    loading={batchSourcesLoading}
                    error={batchSourcesError}
                    onRetry={() => void loadBatchOptions()}
                    ariaLabel="Lote existente para el ajuste"
                    emptyHint="Crea un lote nuevo para registrar esta entrada."
                  />
                )}
              </div>
            )}

            {requiresLot && adjustmentType === 'subtract' && parsedQty > 0 && (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-medium">Origen de la salida</p>
                <p className="text-xs text-slate-500">Para merma puedes seleccionar lotes caducados.</p>
                <Button type="button" variant="outline" className="w-full" onClick={() => setShowSources(true)}>
                  {allocations.reduce((sum, allocation) => sum + allocation.quantity, 0) === parsedQty
                    ? 'Distribución confirmada'
                    : 'Seleccionar stock libre / lotes'}
                </Button>
              </div>
            )}

            {/* Tipo de ajuste */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-inherit">Tipo de ajuste:</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => handleAdjustmentTypeChange('add')}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all ${adjustmentType === 'add'
                      ? 'border-green-500 bg-primary/10 text-primary'
                      : 'border-border text-inherit opacity-70 hover:border-border'
                    }`}
                >
                  <Plus className="h-5 w-5" />
                  <span className="text-xs font-medium">Entrada</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleAdjustmentTypeChange('subtract')}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all ${adjustmentType === 'subtract'
                      ? 'border-red-500 bg-destructive/10 text-red-500'
                      : 'border-border text-inherit opacity-70 hover:border-border'
                    }`}
                >
                  <Minus className="h-5 w-5" />
                  <span className="text-xs font-medium">Salida</span>
                </button>
              </div>
            </div>

            {/* Cantidad */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-inherit">Cantidad:</label>
              <Input
                type="number"
                min="1"
                value={adjustmentQuantity}
                onChange={(e) => {
                  const val = e.target.value
                  if (val === '') {
                    setAdjustmentQuantity('')
                  } else {
                    setAdjustmentQuantity(String(Math.abs(parseInt(val) || 0) || ''))
                  }
                }}
                placeholder="Ej: 5"
                className={willGoNegative ? 'border-red-400 focus-visible:ring-red-400' : ''}
              />
              {willGoNegative && (
                <p className="flex items-center gap-1 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  La cantidad supera el stock disponible ({currentStock} u.)
                </p>
              )}
            </div>

            {/* Motivo */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-inherit">Motivo:</label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {COMMON_REASONS.map((reason) => (
                  <button
                    type="button"
                    key={reason}
                    onClick={() => setAdjustmentReason(reason)}
                    className={`rounded-full px-2.5 py-0.5 text-xs transition-colors ${adjustmentReason === reason
                        ? 'bg-slate-800 text-white'
                        : 'bg-primary/10 text-inherit opacity-70 hover:bg-primary/10'
                      }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <Input
                type="text"
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="O escribe un motivo personalizado..."
              />
            </div>

            {/* Preview del resultado */}
            {adjustmentQuantity && parsedQty > 0 && (
              <div
                className={`rounded-lg px-4 py-3 text-sm ${willGoNegative
                    ? 'bg-destructive/10 text-red-500'
                    : adjustmentType === 'add'
                      ? 'bg-primary/10 text-primary'
                      : 'bg-orange-500/10 text-orange-500'
                  }`}
              >
                <span>Nuevo stock estimado: </span>
                <strong>
                  {newStock} unidad{newStock !== 1 ? 'es' : ''}
                </strong>
              </div>
            )}

            <Button
              onClick={handleApply}
              disabled={
                !adjustmentQuantity || !adjustmentReason || willGoNegative || parsedQty === 0
                || (requiresLot && adjustmentType === 'add' && (
                  batchMode === 'new' ? (!batchNumber.trim() || !expirationDate) : !existingBatchId
                ))
                || (requiresLot && adjustmentType === 'subtract'
                  && allocations.reduce((sum, allocation) => sum + allocation.quantity, 0) !== parsedQty)
              }
              className={`w-full ${adjustmentType === 'add'
                  ? 'bg-primary hover:bg-primary/80 cursor-pointer'
                  : 'bg-red-600 hover:bg-red-700'
                }`}
            >
              {adjustmentType === 'add' ? (
                <>
                  <Plus className="mr-1.5 h-4 w-4" /> Registrar Entrada
                </>
              ) : (
                <>
                  <Minus className="mr-1.5 h-4 w-4" /> Registrar Salida
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
      <InventorySourceModal
        open={showSources}
        onOpenChange={setShowSources}
        product={productId && selectedItem ? { id: productId, name: selectedItem.name } : null}
        quantity={Math.max(1, parsedQty)}
        currentAllocations={allocations}
        quantityMode="fixed"
        allowExpired
        loadSources={loadSources}
        onConfirm={({ allocations: nextAllocations }) => setAllocations(nextAllocations)}
      />
    </Dialog>
  )
}
